import axios from "axios";
import config from "../config";
import { ApiSuccess } from "./api_helper";

const apiBase = config.api.API_URL;

export type ApiMessageHistoryItem = {
  id: number;
  source: string;
  message_type: string | null;
  status: string;
  to_email: string;
  subject: string;
  scheduled_at: string | null;
  sent_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  skip_reason: string | null;
  provider_message_id: string | null;
  created_at: string | null;
  api_token_id?: number | null;
  api_token?: {
    id: number;
    name: string;
    token_prefix: string | null;
  } | null;
};

export type ApiMessageTokenOption = {
  id: number;
  name: string;
  token_prefix?: string | null;
};

export type ApiMessageEngagement7d = {
  sent: number;
  opened: number;
  clicked: number;
};

export type ApiMessageHistoryMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  failed_today?: number;
  pending_count?: number;
  /** Idade (s) do pending mais antigo — alerta se fila da plataforma atrasou. */
  oldest_pending_age_seconds?: number | null;
  engagement_7d?: ApiMessageEngagement7d;
  top_failed_token?: {
    id: number;
    name: string;
    token_prefix?: string | null;
    count: number;
  } | null;
};

export type ApiMessageHistoryPayload = {
  items: ApiMessageHistoryItem[];
  meta: ApiMessageHistoryMeta;
};

export type ApiMessageHistoryStatusFilter =
  | "all"
  | "pending"
  | "sent"
  | "failed"
  | "skipped";

/**
 * Histórico JWT de envios 1:1 (API avulsa ou welcome) — LAC-122 / LAC-120.
 * Interceptor devolve envelope { success, data } — DOC 09 § 7.2.
 */
export async function fetchApiMessageHistory(params: {
  source: "api" | "welcome";
  page?: number;
  per_page?: number;
  status?: ApiMessageHistoryStatusFilter;
  created_from?: string;
  created_to?: string;
  api_token_id?: number | null;
}): Promise<ApiSuccess<ApiMessageHistoryPayload>> {
  const q = new URLSearchParams();
  q.set("source", params.source);
  if (params.page) q.set("page", String(params.page));
  if (params.per_page) q.set("per_page", String(params.per_page));
  if (params.status && params.status !== "all") {
    q.set("status", params.status);
  }
  if (params.created_from) q.set("created_from", params.created_from);
  if (params.created_to) q.set("created_to", params.created_to);
  if (params.api_token_id && params.api_token_id > 0) {
    q.set("api_token_id", String(params.api_token_id));
  }

  return axios.get(`${apiBase}/me/api-messages?${q.toString()}`);
}

/** Download CSV (blob) — Authorization via interceptor axios. */
export async function downloadApiMessageHistoryCsv(params: {
  source: "api" | "welcome";
  status?: ApiMessageHistoryStatusFilter;
  created_from?: string;
  created_to?: string;
  api_token_id?: number | null;
}): Promise<void> {
  const q = new URLSearchParams();
  q.set("source", params.source);
  if (params.status && params.status !== "all") {
    q.set("status", params.status);
  }
  if (params.created_from) q.set("created_from", params.created_from);
  if (params.created_to) q.set("created_to", params.created_to);
  if (params.api_token_id && params.api_token_id > 0) {
    q.set("api_token_id", String(params.api_token_id));
  }

  const data = await axios.get(
    `${apiBase}/me/api-messages/export?${q.toString()}`,
    { responseType: "blob" }
  );

  const blob =
    data instanceof Blob
      ? data
      : new Blob([data as unknown as BlobPart], {
          type: "text/csv;charset=utf-8",
        });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `api-messages-${params.source}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Reenfileira failed — cota só se já liberada no fail. */
export async function retryApiMessage(
  id: number
): Promise<ApiSuccess<ApiMessageHistoryItem>> {
  return axios.post(`${apiBase}/me/api-messages/${id}/retry`);
}

/** Reenfileira falhas em lote (opcionalmente de um token). */
export async function retryFailedApiMessages(params?: {
  api_token_id?: number | null;
  limit?: number;
}): Promise<
  ApiSuccess<{
    retried_count: number;
    retried_ids: number[];
    error_count: number;
    errors: Array<{ id: number; code: string; message: string }>;
  }>
> {
  return axios.post(`${apiBase}/me/api-messages/retry-failed`, {
    api_token_id: params?.api_token_id || undefined,
    limit: params?.limit ?? 25,
  });
}

/** Motivo legível — jargão técnico vira copy i18n quando mapear. */
export function skipReasonLabel(
  reason: string | null,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  if (!reason) return "—";
  const key = `apiMessages.history.reason.${reason}`;
  const translated = t(key, { defaultValue: "" });
  if (translated && translated !== key) return translated;
  return reason.length > 48 ? `${reason.slice(0, 45)}…` : reason;
}

export function parseApiMessagesQueryStatus(
  raw: string | null
): ApiMessageHistoryStatusFilter | null {
  if (!raw) return null;
  const v = raw.toLowerCase();
  if (["failed", "pending", "sent", "skipped", "all"].includes(v)) {
    return v as ApiMessageHistoryStatusFilter;
  }
  return null;
}

/** Deep-link ?api_token_id=123 — compartilhar filtro. */
export function parseApiTokenIdQuery(raw: string | null): number | null {
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

/** Mesma regra do accept marketing (placeholder ou /t/cu/). */
export function htmlHasUnsubMarker(html: string | null | undefined): boolean {
  if (!html) return false;
  return (
    html.includes("{{brenvio_track_unsub}}") || html.includes("/t/cu/")
  );
}
